import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, BarChart3, MessageSquare, Zap, QrCode, Users, Bell, LayoutDashboard, Globe, Shield, Sparkles, TrendingUp, ChevronDown, Check, Twitter, Linkedin, Instagram, Mail, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import bgVideo from '@/assets/bg-video.mp4';
import coffeeBg from '@/assets/coffee-bg.jpg';
// Note: Coffee icon is still used for feature icons, not the logo
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { PLANS, formatPrice, YEARLY_DISCOUNT_PERCENT, getEffectiveMonthlyPrice, type BillingInterval } from '@/lib/constants/plans';

const Home = () => {
  const { user } = useAuth();
  const { t, translations } = useTranslation();
  const [activeSection, setActiveSection] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');

  // Track active section based on scroll position
  useEffect(() => {
    const sections = ['how-it-works', 'benefits', 'what-makes-different', 'pricing'];

    const handleScroll = () => {
      if (window.scrollY < 100) {
        setActiveSection('');
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0
      }
    );

    sections.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Authenticated users are redirected to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="border-b border-amber-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setActiveSection('');
            }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label="Scroll to top"
          >
            <img src="/logo.png" alt="RoastPulse" className="w-8 h-8" />
            <span className="text-xl font-serif font-bold text-stone-800">RoastPulse</span>
          </button>
          <nav className="hidden md:flex items-center gap-6">
            <a
              href="#how-it-works"
              className={cn(
                "text-sm font-medium transition-colors relative py-1",
                activeSection === 'how-it-works'
                  ? "text-amber-600"
                  : "text-stone-600 hover:text-stone-800"
              )}
            >
              {t('home.howItWorks')}
              {activeSection === 'how-it-works' && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber-600 rounded-full" />
              )}
            </a>
            <a
              href="#benefits"
              className={cn(
                "text-sm font-medium transition-colors relative py-1",
                activeSection === 'benefits'
                  ? "text-amber-600"
                  : "text-stone-600 hover:text-stone-800"
              )}
            >
              {t('home.benefits')}
              {activeSection === 'benefits' && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber-600 rounded-full" />
              )}
            </a>
            <a
              href="#what-makes-different"
              className={cn(
                "text-sm font-medium transition-colors relative py-1",
                activeSection === 'what-makes-different'
                  ? "text-amber-600"
                  : "text-stone-600 hover:text-stone-800"
              )}
            >
              {t('home.whatMakesDifferent')}
              {activeSection === 'what-makes-different' && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber-600 rounded-full" />
              )}
            </a>
            <a
              href="#pricing"
              className={cn(
                "text-sm font-medium transition-colors relative py-1",
                activeSection === 'pricing'
                  ? "text-amber-600"
                  : "text-stone-600 hover:text-stone-800"
              )}
            >
              {t('home.simplePricing')}
              {activeSection === 'pricing' && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber-600 rounded-full" />
              )}
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden md:block">
              <Button variant="ghost">{t('home.signIn')}</Button>
            </Link>
            <Link to="/signup" className="hidden md:block">
              <Button>{t('home.getStarted')}</Button>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-stone-600 hover:text-stone-800 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-amber-100 sticky top-[65px] z-40 overflow-hidden"
          >
            <nav className="flex flex-col px-6 py-4 space-y-4">
              <a
                href="#how-it-works"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "text-sm font-medium transition-colors py-2",
                  activeSection === 'how-it-works'
                    ? "text-amber-600"
                    : "text-stone-600"
                )}
              >
                {t('home.howItWorks')}
              </a>
              <a
                href="#benefits"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "text-sm font-medium transition-colors py-2",
                  activeSection === 'benefits'
                    ? "text-amber-600"
                    : "text-stone-600"
                )}
              >
                {t('home.benefits')}
              </a>
              <a
                href="#what-makes-different"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "text-sm font-medium transition-colors py-2",
                  activeSection === 'what-makes-different'
                    ? "text-amber-600"
                    : "text-stone-600"
                )}
              >
                {t('home.whatMakesDifferent')}
              </a>
              <a
                href="#pricing"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "text-sm font-medium transition-colors py-2",
                  activeSection === 'pricing'
                    ? "text-amber-600"
                    : "text-stone-600"
                )}
              >
                {t('home.simplePricing')}
              </a>
              <div className="pt-4 border-t border-stone-100 flex flex-col gap-3">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full">{t('home.signIn')}</Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full">{t('home.getStarted')}</Button>
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative w-full h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={bgVideo} type="video/mp4" />
        </video>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Content */}
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-tight"
          >
            {t('home.heroTitle')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white/80 mt-6 text-lg sm:text-xl max-w-2xl mx-auto"
          >
            {t('home.heroSubtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mt-10"
          >
            <Link to="/signup">
              <Button size="lg" className="gap-2 text-base px-8">
                {t('home.startFreeTrial')}
              </Button>
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-6 text-sm text-white/70"
          >
            {t('home.freeNote')}
          </motion.p>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-8 h-8 text-white/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-serif font-bold text-center text-stone-800 mb-16"
          >
            {t('home.howItWorks')}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="relative w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <QrCode className="w-8 h-8 text-amber-600" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-amber-600 text-white text-sm font-bold rounded-full flex items-center justify-center">
                  1
                </span>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.step1Title')}
              </h3>
              <p className="text-stone-600">
                {t('home.step1Desc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center"
            >
              <div className="relative w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-8 h-8 text-amber-600" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-amber-600 text-white text-sm font-bold rounded-full flex items-center justify-center">
                  2
                </span>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.step2Title')}
              </h3>
              <p className="text-stone-600">
                {t('home.step2Desc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center"
            >
              <div className="relative w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-8 h-8 text-amber-600" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-amber-600 text-white text-sm font-bold rounded-full flex items-center justify-center">
                  3
                </span>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.step3Title')}
              </h3>
              <p className="text-stone-600">
                {t('home.step3Desc')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-serif font-bold text-stone-800 mb-4">
              {t('home.benefits')}
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto">
              {t('home.benefitsSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
            >
              <QrCode className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.effortlessFeedback')}
              </h3>
              <p className="text-stone-600">
                {t('home.effortlessFeedbackDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
            >
              <Users className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.understandCustomers')}
              </h3>
              <p className="text-stone-600">
                {t('home.understandCustomersDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
            >
              <Sparkles className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.aiInsightsTitle')}
              </h3>
              <p className="text-stone-600">
                {t('home.aiInsightsDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
            >
              <Bell className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.earlyWarning')}
              </h3>
              <p className="text-stone-600">
                {t('home.earlyWarningDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
            >
              <LayoutDashboard className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.analyticsDashboard')}
              </h3>
              <p className="text-stone-600">
                {t('home.analyticsDashboardDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
            >
              <Globe className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-3">
                {t('home.multilingualSupport')}
              </h3>
              <p className="text-stone-600">
                {t('home.multilingualSupportDesc')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Different - Parallax Section */}
      <section 
        id="what-makes-different"
        className="py-24 relative bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${coffeeBg})` }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/70" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-serif font-bold text-center text-white mb-16"
          >
            {t('home.whatMakesDifferent')}
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <Coffee className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('home.madeForCoffee')}
              </h3>
              <p className="text-white/80">
                {t('home.madeForCoffeeDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <Zap className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('home.zeroFriction')}
              </h3>
              <p className="text-white/80">
                {t('home.zeroFrictionDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <Sparkles className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('home.aiWorksForYou')}
              </h3>
              <p className="text-white/80">
                {t('home.aiWorksForYouDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <BarChart3 className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('home.actionableData')}
              </h3>
              <p className="text-white/80">
                {t('home.actionableDataDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <TrendingUp className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('home.growsWithYou')}
              </h3>
              <p className="text-white/80">
                {t('home.growsWithYouDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <Shield className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('home.respectsPrivacy')}
              </h3>
              <p className="text-white/80">
                {t('home.respectsPrivacyDesc')}
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link to="/signup">
              <Button size="lg" className="px-8 text-base">
                {t('home.startFreeTrial')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-serif font-bold text-stone-800 mb-4">
              {t('home.simplePricing')}
            </h2>
            <p className="text-stone-600 mb-10 max-w-2xl mx-auto">
              {t('home.pricingSubtitle')}
            </p>
            
            {/* Billing Toggle */}
            <div className="relative flex items-center justify-center gap-3 mb-12 pt-4">
              <span className={cn(
                "text-sm font-medium transition-colors",
                billingInterval === 'monthly' ? "text-stone-800" : "text-stone-400"
              )}>
                {t('billing.monthly')}
              </span>
              <button
                onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
                className={cn(
                  "relative w-14 h-7 rounded-full transition-colors duration-200",
                  billingInterval === 'yearly' ? "bg-amber-500" : "bg-stone-300"
                )}
                role="switch"
                aria-checked={billingInterval === 'yearly'}
                aria-label="Toggle billing interval"
              >
                <span
                  className={cn(
                    "absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ease-in-out",
                    billingInterval === 'yearly' ? "translate-x-7" : "translate-x-0"
                  )}
                />
              </button>
              <span className={cn(
                "text-sm font-medium transition-colors",
                billingInterval === 'yearly' ? "text-stone-800" : "text-stone-400"
              )}>
                {t('billing.yearly')}
              </span>
              <span 
                className={cn(
                  "absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-semibold text-white bg-green-500 px-2 py-0.5 rounded-full transition-opacity duration-200",
                  billingInterval === 'yearly' ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
              >
                {t('billing.savePercent', { percent: YEARLY_DISCOUNT_PERCENT })}
              </span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Free Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -4 }}
              className="border border-stone-200 rounded-2xl p-6 bg-white hover:shadow-lg transition-shadow"
            >
              <h3 className="text-lg font-semibold text-stone-800">{PLANS.free.name}</h3>
              <p className="text-3xl font-bold text-stone-800 mt-4">{formatPrice(0)}</p>
              <p className="text-stone-500 text-sm mt-1">{t('home.forever')}</p>
              <ul className="text-left text-stone-600 mt-6 space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.free.products} {t('home.product')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.free.lots} {t('home.lots')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.free.feedbackPerMonth} feedback/{t('billing.month')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {t('home.freeFeature3')}
                </li>
              </ul>
              <div className="mt-6">
                <Link to="/signup">
                  <Button variant="outline" className="w-full">{t('home.getStarted')}</Button>
                </Link>
              </div>
            </motion.div>

            {/* Starter Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -4 }}
              className="border border-stone-200 rounded-2xl p-6 bg-white hover:shadow-lg transition-shadow"
            >
              <h3 className="text-lg font-semibold text-stone-800">{PLANS.starter.name}</h3>
              <p className="text-3xl font-bold text-stone-800 mt-4">
                {billingInterval === 'yearly' 
                  ? formatPrice(getEffectiveMonthlyPrice('starter'))
                  : formatPrice(PLANS.starter.pricing.monthly.price)}
              </p>
              <p className="text-stone-500 text-sm mt-1">
                /{t('billing.month')}{billingInterval === 'yearly' && `, ${t('billing.billedYearly')}`}
              </p>
              <ul className="text-left text-stone-600 mt-6 space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.starter.products} {t('home.products')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.starter.lots} {t('home.lots')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.starter.feedbackPerMonth} feedback/{t('billing.month')}
                </li>
                <li>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    {t('home.starterFeature3')}
                  </div>
                  <ul className="ml-6 mt-1.5 space-y-1">
                    {((translations.home as Record<string, unknown>).starterAiFeatures as string[]).map((feature, idx) => (
                      <li key={idx} className="text-xs text-stone-500">• {feature}</li>
                    ))}
                  </ul>
                </li>
              </ul>
              <div className="mt-6">
                <Link to="/signup">
                  <Button variant="outline" className="w-full">{t('home.getStarted')}</Button>
                </Link>
              </div>
            </motion.div>

            {/* Pro Plan - Popular */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -4 }}
              className="border-2 border-amber-500 rounded-2xl p-6 relative bg-amber-50/50 hover:shadow-lg transition-shadow"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {t('home.popular')}
              </div>
              <h3 className="text-lg font-semibold text-stone-800">{PLANS.pro.name}</h3>
              <p className="text-3xl font-bold text-stone-800 mt-4">
                {billingInterval === 'yearly' 
                  ? formatPrice(getEffectiveMonthlyPrice('pro'))
                  : formatPrice(PLANS.pro.pricing.monthly.price)}
              </p>
              <p className="text-stone-500 text-sm mt-1">
                /{t('billing.month')}{billingInterval === 'yearly' && `, ${t('billing.billedYearly')}`}
              </p>
              <ul className="text-left text-stone-600 mt-6 space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.pro.products === 'unlimited'
                    ? t('billing.unlimitedProducts')
                    : `${PLANS.pro.products} ${t('home.products')}`}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.pro.lots === 'unlimited'
                    ? t('billing.unlimitedLots')
                    : `${PLANS.pro.lots} ${t('home.lots')}`}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  {PLANS.pro.feedbackPerMonth} feedback/{t('billing.month')}
                </li>
                <li>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    {t('home.proFeature3')}
                  </div>
                  <ul className="ml-6 mt-1.5 space-y-1">
                    <li className="text-xs text-stone-500 italic">• {t('home.proAiIncludesStarter')}</li>
                    {((translations.home as Record<string, unknown>).proAiFeatures as string[]).map((feature, idx) => (
                      <li key={idx} className="text-xs text-stone-500">• {feature}</li>
                    ))}
                  </ul>
                </li>
              </ul>
              <div className="mt-6">
                <Link to="/signup">
                  <Button className="w-full">{t('home.getStarted')}</Button>
                </Link>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-12"
          >
            <p className="text-sm text-stone-500">
              {t('home.allPlansInclude')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="RoastPulse" className="w-8 h-8 brightness-0 invert" />
                <span className="text-xl font-serif font-bold text-white">RoastPulse</span>
              </div>
              <p className="text-sm leading-relaxed mb-6">
                {t('home.footerTagline')}
              </p>
              {/* Social Links */}
              <div className="flex items-center gap-4">
                <a href="#" className="hover:text-white transition-colors" aria-label="Twitter">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="hover:text-white transition-colors" aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="hover:text-white transition-colors" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="mailto:hello@roastpulse.com" className="hover:text-white transition-colors" aria-label="Email">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Product Column */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('home.footerProduct')}</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="#how-it-works" className="hover:text-white transition-colors">
                    {t('home.howItWorks')}
                  </a>
                </li>
                <li>
                  <a href="#benefits" className="hover:text-white transition-colors">
                    {t('home.footerFeatures')}
                  </a>
                </li>
                <li>
                  <a href="#what-makes-different" className="hover:text-white transition-colors">
                    {t('home.whatMakesDifferent')}
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white transition-colors">
                    {t('home.footerPricing')}
                  </a>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors">
                    {t('home.signIn')}
                  </Link>
                </li>
                <li>
                  <Link to="/signup" className="hover:text-white transition-colors">
                    {t('home.getStarted')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('home.footerCompany')}</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('home.footerAbout')}
                  </a>
                </li>
                <li>
                  <a href="mailto:hello@roastpulse.com" className="hover:text-white transition-colors">
                    {t('home.footerContact')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('home.footerLegal')}</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    {t('home.footerPrivacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    {t('home.footerTerms')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-stone-800">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-stone-500">
                {t('home.copyright', { year: new Date().getFullYear().toString() })}
              </p>
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Coffee className="w-4 h-4" />
                <span>Made with love for coffee roasters</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
